import _ from "lodash";
import {
	AdjustmentsFrequency,
	BCVConfigType,
	ContractDetailType,
	ContractPeriodType,
	ContractStatus,
	SpotMarket,
	TradeType,
} from "../../../requests_cm/gecoContractsService/types";
import {
	getContractPeriodFromId,
	getContractPeriodIndexFromId,
} from "./common";
import { PricingAddons } from "../../../requests_cm/gecoReferentialService/types";

export interface GeneralTabFormikData {
	status?: ContractStatus;
	name?: string;
	goo_price?: number;
	trade_date?: string;
	start_date?: string;
	end_date?: string;
	test_phase?: boolean;
	trade_type?: TradeType;
	base_prod_capacity?: number;
	commodity?: string;
	has_adjustments?: boolean;
	invoice_decomposition?: boolean;
	adjustment_frequency?: AdjustmentsFrequency;
	bcv_config?: BCVConfigType;
	bcv_config_goo?: BCVConfigType;
	pricing_addons: PricingAddons[];
	spot_market?: SpotMarket;
	country?: string;
}

export const generalTabFormikInitialValueMapper = (
	data: ContractDetailType | undefined,
	contract_period_id: number
): GeneralTabFormikData => {
	const values = {
		status: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.status,
		country: data?.country,
		name: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.name,
		goo_price: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.goo_price,
		trade_date: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.trade_date,
		start_date: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.start_date,
		end_date: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.end_date,
		test_phase: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.test_phase,
		trade_type: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.trade_type,
		base_prod_capacity: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.ppa_base_physical?.base_prod_capacity,
		commodity: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.commodity,
		has_adjustments:
			getContractPeriodFromId(
				data?.contract_periods || [],
				contract_period_id
			)?.invoicing?.has_adjustments || false,
		invoice_decomposition:
			getContractPeriodFromId(
				data?.contract_periods || [],
				contract_period_id
			)?.invoicing?.invoice_decomposition || false,
		adjustment_frequency: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.invoicing?.adjustment_frequency,
		bcv_config: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.bcv_config,
		bcv_config_goo: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.bcv_config_goo,
		pricing_addons:
			getContractPeriodFromId(
				data?.contract_periods || [],
				contract_period_id
			)?.pricing_addons || [],
		spot_market: getContractPeriodFromId(
			data?.contract_periods || [],
			contract_period_id
		)?.spot_market,
	};
	return values;
};

export const formikDataToContractModelMapper = (
	data: GeneralTabFormikData,
	originData: ContractDetailType,
	contract_period_id: number
): ContractDetailType => {
	const updatedContract = _.cloneDeep(originData);
	const contractPeriodIndex = getContractPeriodIndexFromId(
		originData.contract_periods,
		contract_period_id
	);
	const updatedContractPeriod: ContractPeriodType = {
		...(getContractPeriodFromId(
			originData.contract_periods,
			contract_period_id
		) as ContractPeriodType),
		name: data.name as string,
		status: data.status,
		trade_date: data.trade_date as string,
		start_date: data.start_date as string,
		end_date: data.end_date as string,
		test_phase: data.test_phase as boolean,
		trade_type: data.trade_type as TradeType,
		...(data.commodity ? { commodity: data.commodity } : {}),
		goo_price: (data.goo_price as number) || 0,
		bcv_config: {
			id: data.bcv_config?.id,
			name: data.bcv_config?.name?.split("|")[0].trimEnd(),
		},
		pricing_addons: data.pricing_addons || [],
		spot_market: data?.spot_market as SpotMarket,
	};

	if (data.bcv_config_goo) {
		updatedContractPeriod["bcv_config_goo"] = {
			id: data.bcv_config_goo?.id,
			name: data.bcv_config_goo?.name?.split("|")[0].trimEnd(),
		};
	}

	// invoicing
	const newInvoicing = {
		...updatedContractPeriod.invoicing,
	};

	newInvoicing.has_adjustments = data.has_adjustments as boolean;
	newInvoicing.invoice_decomposition = data.invoice_decomposition as boolean;
	newInvoicing.adjustment_frequency =
		data.adjustment_frequency as AdjustmentsFrequency;

	updatedContractPeriod.invoicing = newInvoicing;
	if (data.trade_type === TradeType.PPA_BASE_PHYSICAL) {
		const newPpaBasePhysical = {
			...(updatedContractPeriod?.ppa_base_physical || {}),
		};
		newPpaBasePhysical.base_prod_capacity =
			data.base_prod_capacity as number;
		updatedContractPeriod.ppa_base_physical = newPpaBasePhysical;
	}

	updatedContract.contract_periods[contractPeriodIndex] =
		updatedContractPeriod;

	return updatedContract;
};
